/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Fetch geos for a map/list page
 */

import { geos } from '@zcode/nr/es/shared/api'
import Geos from '@zcode/nr/es/shared/bizos/geos'
import type { Geo } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { logger } from '../../../logger'
import { receiveGeos } from '../actions'

const fixGeos = (url: string, geos: Geo): Geo => {
  // if we are asking for a hood, use the city object that belongs to the geos hood since we know
  // this neighborhood to be more accurate instead of guessing the city from the geos cities array
  const { cities = [], hood } = geos
  if (hood?.path && hood.path === url) {
    const hoodCityId = hood.cityId
    const betterCityIdx = cities.findIndex((city) => city.cityId === hoodCityId)
    // if a city matches the hood's city, move it to the front of cities array if it is not
    // already there. we do this because the geos bizos defines city as the cities array's
    // first element
    if (betterCityIdx > 0) {
      const newCities = cities.slice()
      const matchingCity = newCities.splice(betterCityIdx, 1)
      // @ts-expect-error Geo type needs to extend the Bizo type
      const newGeos = geos.toJSON()

      newGeos.cities = matchingCity
        .concat(newCities)
        // @ts-expect-error MCity type needs to extend the Bizo type
        .map((city) => (city.toJSON ? city.toJSON() : city))
      return Geos.fromJS(newGeos)
    }
    return geos
  }
  return geos
}

export interface GeosParams {
  basesLimit?: number
  citiesLimit?: number
  hoodsLimit?: number
  univsLimit?: number
  url: string
  zipsLimit?: number
}

export const loadGeos = (urlOrParams: string | GeosParams) => async (dispatch: Dispatch) => {
  const url = typeof urlOrParams === 'object' ? urlOrParams.url : urlOrParams
  const params =
    typeof urlOrParams === 'object'
      ? urlOrParams
      : {
          url,
          citiesLimit: 20,
          basesLimit: 0,
          hoodsLimit: 20,
          univsLimit: 12,
          zipsLimit: 20,
        }

  return await geos
    .get(params)
    .then((geos) => {
      const betterGeos = fixGeos(url, geos)

      dispatch(receiveGeos(betterGeos))
      return betterGeos
    })
    .catch(async (error: unknown) => {
      logger.debug(
        'loadGeos failed:',
        url,
        error && typeof error === 'object' && 'data' in error && error.data ? error.data : error
      )
      return await Promise.reject(error)
    })
}
